/**
 * Web + Electron local notifications.
 * Electron: main-process Notification via IPC (more reliable on desktop).
 * Browser: Notification API.
 */
import { isElectronRuntime } from '../utils/isElectron';

export type LocalPushPayload = {
  id?: string;
  title: string;
  body: string;
  data?: Record<string, string>;
};

async function showElectronNotification(
  payload: LocalPushPayload,
): Promise<boolean> {
  try {
    const ipc = (window as any)?.electron?.ipcRenderer;
    if (!ipc?.invoke) return false;
    await ipc.invoke('show-notification', {
      title: payload.title,
      body: payload.body,
      data: payload.data,
    });
    return true;
  } catch (err) {
    console.warn('Electron notification failed:', err);
    return false;
  }
}

function hasBrowserPermission(): boolean {
  if (typeof Notification === 'undefined') return false;
  return Notification.permission === 'granted';
}

async function requestBrowserPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    const result = await Notification.requestPermission();
    return result === 'granted';
  } catch {
    return false;
  }
}

async function showBrowserNotification(
  payload: LocalPushPayload,
): Promise<boolean> {
  try {
    if (!hasBrowserPermission()) return false;
    // eslint-disable-next-line no-new
    new Notification(payload.title, {
      body: payload.body,
      tag: payload.id,
      data: payload.data,
    });
    return true;
  } catch (err) {
    console.warn('Browser notification failed:', err);
    return false;
  }
}

export async function showLocalPushNotification(
  payload: LocalPushPayload,
): Promise<boolean> {
  if (isElectronRuntime()) {
    const shown = await showElectronNotification(payload);
    if (shown) return true;
  }
  return showBrowserNotification(payload);
}

export async function hasLocalNotificationPermission(): Promise<boolean> {
  if (isElectronRuntime()) return true;
  return hasBrowserPermission();
}

export async function requestLocalNotificationPermission(): Promise<boolean> {
  if (isElectronRuntime()) return true;
  return requestBrowserPermission();
}

/** Check-only — does not prompt. */
export async function ensureLocalNotificationPermission(): Promise<boolean> {
  return hasLocalNotificationPermission();
}
